import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
// Add missing imports
import { Link } from "react-router-dom";
import {
  FaHome,
  FaFolder,
  FaFileAlt,
  FaUser,
  FaSignOutAlt,
} from "react-icons/fa";
import logo from "../../../assets/irc-logo-rb.png";
// Keep existing import
import { FaArrowLeft } from "react-icons/fa";
import Sidebar from "./Sidebar";

const ForumEvaluation = () => {
  const { idcompetition, idprojet } = useParams();
  const navigate = useNavigate();

  // Sidebar state and handlers referenced in JSX
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState("competition");
  const handleToggleSidebar = () => setSidebarOpen((s) => !s);
  const handleItemClick = (item) => setSelectedItem(item);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [evaluationId, setEvaluationId] = useState(null);
  const [evaluationTitle, setEvaluationTitle] = useState("");
  const [criteres, setCriteres] = useState([]);
  const [sousCriteres, setSousCriteres] = useState({}); // { [ID_criteres]: [...] }
  const [comments, setComments] = useState({}); // { [ID_sous_critres]: string }
  const [notes, setNotes] = useState({}); // { [ID_sous_critres]: number }
  const [projectInfo, setProjectInfo] = useState(null);
  const [competitionTitle, setCompetitionTitle] = useState("");
  const [currentCritereIndex, setCurrentCritereIndex] = useState(0);
  const [relatedData, setRelatedData] = useState({
    equipe: [],
    membres: [],
    planning: [],
    taches: [],
    budget: [],
  });
  const [relatedLoaded, setRelatedLoaded] = useState(false);
  const [alreadyEvaluated, setAlreadyEvaluated] = useState(false);
  // NEW: map each sous-critère ID to its max note (from DB, fallback 20)
  const [maxNoteBySous, setMaxNoteBySous] = useState({});

  // Helper to prettify keys for PDF
  const prettifyKey = (k) =>
    k
      .replace(/^[a-z]/, (c) => c.toUpperCase())
      .replace(/_/g, " ")
      .replace(/id/gi, "ID");

  const handleDownloadPdf = async () => {
    if (!projectInfo || !relatedLoaded) return;
    try {
      const jspdf = await import('jspdf');
      const { jsPDF } = jspdf;
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });

      // Constants & styling tokens
      const PAGE_W = doc.internal.pageSize.getWidth();
      const PAGE_H = doc.internal.pageSize.getHeight();
      const MARGIN_X = 46;
      const CONTENT_W = PAGE_W - MARGIN_X * 2;
      const HEADER_H = 60;
      const ACCENT = { r: 11, g: 122, b: 62 };
      const LIGHT_BG = [245, 247, 250];
      const BORDER_CLR = [214, 219, 223];
      let y = HEADER_H + 26;

      // NEW: consistent section spacing
      const TOP_Y = HEADER_H + 26;
      const SECTION_GAP = 24;
      const sectionBreak = () => {
        // add spacing between sections except at top of a new page
        if (y > TOP_Y) {
          y += SECTION_GAP;
          ensureSpace(0);
        }
      };

      const fmtNumber = (v) => {
        if (v == null || v === '') return '';
        const n = Number(String(v).replace(/[^0-9.]/g,''));
        if (isNaN(n)) return v;
        return n.toLocaleString('en-US'); // comma separated
      };

      const formatDate = (val) => {
        if (!val) return '';
        const s = String(val);
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!m) return s;
        return `${m[3]}-${m[2]}-${m[1]}`; // dd-mm-yyyy
      };

      // Load logo once, produce faded version & capture dimensions
      const loadImageAsDataUrl = (src) => new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const w = img.width, h = img.height;
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h; canvas.getContext('2d').drawImage(img,0,0);
          const full = canvas.toDataURL('image/png');
          const fadeCanvas = document.createElement('canvas');
          fadeCanvas.width = w; fadeCanvas.height = h; const fctx = fadeCanvas.getContext('2d');
          fctx.globalAlpha = 0.15; // a bit stronger so visible
          fctx.drawImage(img,0,0);
          const faded = fadeCanvas.toDataURL('image/png');
          resolve({ full, faded, w, h });
        };
        img.onerror = reject; img.src = src;
      });
      let logoData = null, watermarkLogoData = null, logoW = 0, logoH = 0;
      try { const ld = await loadImageAsDataUrl(logo); logoData = ld.full; watermarkLogoData = ld.faded; logoW = ld.w; logoH = ld.h; } catch {/* ignore */}

      // HEADER (minimal, professional) with watermark
      const drawHeader = () => {
        // Watermark first (center) so header text sits above
        if (watermarkLogoData) {
          // scale watermark to ~55% of page width while keeping aspect
            const maxW = PAGE_W * 0.55;
            const scale = maxW / logoW;
            const wmW = maxW;
            const wmH = logoH * scale;
            const wmX = (PAGE_W - wmW) / 2;
            const wmY = (PAGE_H - wmH) / 2;
            doc.addImage(watermarkLogoData, 'PNG', wmX, wmY, wmW, wmH, undefined, 'FAST');
        }
        doc.setDrawColor(225);
        doc.setLineWidth(.8);
        doc.setFont('helvetica','bold');
        if (logoData) doc.addImage(logoData,'PNG', MARGIN_X, 14, 82, 34);
        doc.setTextColor(0,0,0);
        doc.setFontSize(14);
        const compTitle = competitionTitle || 'Compétition';
        doc.text(compTitle, MARGIN_X + 95, 26, { maxWidth: CONTENT_W - 100 });
        doc.setFont('helvetica','normal');
        doc.setFontSize(12);
        const projTitle = (projectInfo.titre || projectInfo.titre_projet || `Projet #${projectInfo.idprojet}`);
        doc.text(projTitle, MARGIN_X + 95, 42, { maxWidth: CONTENT_W - 100 });
        doc.line(MARGIN_X, HEADER_H, PAGE_W - MARGIN_X, HEADER_H);
      };

      const addFooter = () => {
        const pages = doc.getNumberOfPages();
        for (let i=1;i<=pages;i++){ doc.setPage(i); doc.setDrawColor(230); doc.setLineWidth(.6); doc.line(MARGIN_X, PAGE_H-54, PAGE_W-MARGIN_X, PAGE_H-54); doc.setFontSize(9); doc.setTextColor(120); doc.text(`Page ${i} / ${pages}`, PAGE_W - MARGIN_X, PAGE_H - 34, { align: 'right' }); }
      };

      const ensureSpace = (needed=100) => { if (y + needed > PAGE_H - 70) { doc.addPage(); drawHeader(); y = HEADER_H + 26; } };

      const sectionHeader = (title, opts={}) => {
        // NEW: enforce uniform spacing before every section
        sectionBreak();
        ensureSpace(60);
        doc.setFont('helvetica','bold');
        doc.setFontSize(12.5);
        doc.setTextColor(ACCENT.r, ACCENT.g, ACCENT.b);
        // Accent bar
        doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b);
        doc.rect(MARGIN_X, y - 10, 6, 28, 'F');
        // Title box outline
        doc.setDrawColor(ACCENT.r, ACCENT.g, ACCENT.b);
        doc.roundedRect(MARGIN_X + 10, y - 12, CONTENT_W - 16, 32, 6, 6, 'S');
        doc.text(title.toUpperCase(), MARGIN_X + 24, y + 4);
        y += 32;
        doc.setFont('helvetica','normal');
        doc.setFontSize(10.8);
        doc.setTextColor(40,40,40);
      };

      const paragraph = (text) => {
        if (!text) return; const lines = doc.splitTextToSize(text, CONTENT_W - 24);
        // Shaded box
        const boxTop = y; const lineH = 14; const height = lines.length * lineH + 16;
        ensureSpace(height + 10);
        doc.setFillColor(...LIGHT_BG); doc.setDrawColor(...BORDER_CLR); doc.roundedRect(MARGIN_X, y, CONTENT_W, height, 6,6,'F');
        doc.roundedRect(MARGIN_X, y, CONTENT_W, height, 6,6,'S');
        let innerY = y + 12; doc.setFontSize(10.5); lines.forEach(ln => { doc.text(ln, MARGIN_X + 12, innerY); innerY += lineH; });
        y = boxTop + height + 10; doc.setFontSize(10.8);
      };

      const metaGrid = (pairs) => {
        const filtered = pairs.filter(p => p[1] !== undefined && p[1] !== null && p[1] !== '');
        if (!filtered.length) return;
        const COLS = 2;
        const colWidth = CONTENT_W / COLS;
        const processed = filtered.map(([label, value]) => {
          let val = Array.isArray(value) ? value.join(', ') : String(value);
          // date normalization
            val = formatDate(val);
          const lines = doc.splitTextToSize(val, colWidth - 28);
          return { label, lines, height: lines.length * 12 + 4 };
        });
        // build rows
        const rows = [];
        for (let i=0; i<processed.length; i+=COLS) {
          rows.push(processed.slice(i, i+COLS));
        }
        const totalHeight = rows.reduce((acc,row)=> acc + Math.max(...row.map(c=>c.height)) + 12, 0) + 12;
        ensureSpace(totalHeight + 10);
        doc.setFillColor(255,255,255); doc.setDrawColor(...BORDER_CLR);
        doc.roundedRect(MARGIN_X, y, CONTENT_W, totalHeight, 8,8,'F');
        doc.roundedRect(MARGIN_X, y, CONTENT_W, totalHeight, 8,8,'S');
        let cursorY = y + 16;
        rows.forEach(row => {
          const rowHeight = Math.max(...row.map(c=>c.height));
          row.forEach((cell, idx) => {
            const baseX = MARGIN_X + idx * colWidth + 14;
            doc.setFont('helvetica','bold'); doc.setFontSize(10.2); doc.setTextColor(70,70,70);
            doc.text(`${cell.label}:`, baseX, cursorY);
            const labelWidth = doc.getTextWidth(`${cell.label}: `);
            doc.setFont('helvetica','normal'); doc.setTextColor(30,30,30);
            cell.lines.forEach((ln, iLine) => {
              const lineY = cursorY + (iLine * 12);
              doc.text(ln, baseX + labelWidth, lineY);
            });
          });
          cursorY += rowHeight + 12;
        });
        y += totalHeight + 14;
        doc.setFont('helvetica','normal'); doc.setTextColor(40,40,40); doc.setFontSize(10.8);
      };

      const simpleList = (items) => {
        items.forEach(it => paragraph(it));
      };

      const drawBudgetTable = (rows) => {
        if (!rows || !rows.length) return;
        ensureSpace(80);
        const headers = ['Description','Montant (MAD)'];
        const colWidths = [CONTENT_W * 0.68, CONTENT_W * 0.32];
        const startY = y;
        const rowH = 22;
        // Header background
        doc.setFillColor(ACCENT.r, ACCENT.g, ACCENT.b);
        doc.setTextColor(255,255,255);
        doc.setFont('helvetica','bold');
        doc.rect(MARGIN_X, y, CONTENT_W, rowH, 'F');
        let x = MARGIN_X + 10; doc.text(headers[0], x, y + 14, { maxWidth: colWidths[0]-16 });
        x = MARGIN_X + colWidths[0] + 10; doc.text(headers[1], x, y + 14);
        y += rowH;
        doc.setFont('helvetica','normal'); doc.setFontSize(10.2); doc.setTextColor(30,30,30);
        rows.forEach(r => {
          ensureSpace(rowH + 10);
          const desc = r.valeur_description || r.description || r.Description || '-';
          const amt = fmtNumber(r.valeur_montant || r.montant || r.Montant || r.amount || '');
          // Row background (zebra)
          doc.setFillColor( y/rowH % 2 ? 255 : 249,  y/rowH % 2 ? 255 : 250,  y/rowH % 2 ? 255 : 252 );
          doc.rect(MARGIN_X, y, CONTENT_W, rowH, 'F');
          doc.setDrawColor(240); doc.rect(MARGIN_X, y, CONTENT_W, rowH, 'S');
          doc.text(doc.splitTextToSize(desc, colWidths[0]-20), MARGIN_X + 10, y + 14);
          doc.text(String(amt || '-'), MARGIN_X + colWidths[0] + 10, y + 14);
          y += rowH;
        });
        // Total
        ensureSpace(rowH+10);
        const total = rows.reduce((s,r)=> s + (parseFloat(r.valeur_montant || r.montant || 0) || 0),0);
        doc.setFont('helvetica','bold');
        doc.setFillColor(240,240,240); doc.rect(MARGIN_X, y, CONTENT_W, rowH, 'F');
        doc.rect(MARGIN_X, y, CONTENT_W, rowH, 'S');
        doc.text('TOTAL', MARGIN_X + 10, y + 14);
        doc.text(fmtNumber(total), MARGIN_X + colWidths[0] + 10, y + 14);
        y += rowH + 14; doc.setFont('helvetica','normal');
      };

      const prettifyKey = (k) => k.replace(/_/g,' ').replace(/\b([a-z])/g,(m,c)=>c.toUpperCase()).replace(/Id/gi,'ID');

      drawHeader();

      // Meta grid
      sectionHeader('Informations Projet');
      const auteur = [projectInfo.prenom, projectInfo.nom].filter(Boolean).join(' ') || projectInfo.responsable || '-';
      metaGrid([
        ['Titre', projectInfo.titre || projectInfo.titre_projet],
        ['Auteur', auteur],
        ['Compétition', competitionTitle],
        ['Date Soumission', formatDate(projectInfo.date_debut_projet || projectInfo.date_creation)],
        ['Domaine', projectInfo.Domaine || projectInfo.domaine],
        ['Sous Domaine', projectInfo.sous_domaine || projectInfo.sub_domaine],
        ['Montant Global', fmtNumber(projectInfo.Montant_global || projectInfo.montant_global)],
        ['Subvention Demandée', fmtNumber(projectInfo.Montant_subvention || projectInfo.montant_subvention)],
      ]);

      // Long scientific fields
      const ResumeFr = projectInfo.Resume_francais || projectInfo.resume_francais || projectInfo.resumeFr;
      const ResumeEn = projectInfo.Resume_anglais || projectInfo.resume_anglais || projectInfo.resumeEn;
      const Objectifs = projectInfo.Objectif_general_specifiques || projectInfo.objectif_general_specifiques || projectInfo.objectifs;
      const Methodologie = projectInfo.Methodologie_proposee || projectInfo.methodologie_proposee || projectInfo.methodologie;
      const Resultats = projectInfo.resultats_attendus || projectInfo.Resultats_attendus || projectInfo.resultats;
      const Ethique = projectInfo.considerations_ethiques || projectInfo.Considerations_ethiques || projectInfo.ethiques;

      const sciBlocks = [
        ['Résumé Français', ResumeFr],
        ['Résumé Anglais', ResumeEn],
        ['Objectif général & Objectifs spécifiques', Objectifs],
        ['Méthodologie proposée', Methodologie],
        ['Résultats attendus', Resultats],
        ['Considérations éthiques', Ethique],
      ].filter(([_,v]) => v && String(v).trim());

      if (sciBlocks.length) {
        sectionHeader('Contenu Scientifique');
        sciBlocks.forEach(([lbl,val]) => { ensureSpace(80); doc.setFont('helvetica','bold'); doc.setTextColor(ACCENT.r, ACCENT.g, ACCENT.b); doc.text(lbl, MARGIN_X, y); y += 14; doc.setFont('helvetica','normal'); doc.setTextColor(40,40,40); paragraph(String(val)); });
      }

      // Related data (Team, Members, Planning, Tasks)
      const isISODateTime = (v) => /^(\d{4})-(\d{2})-(\d{2})T/.test(v);
      const isISODate = (v) => /^(\d{4})-(\d{2})-(\d{2})$/.test(v);
      const relatedMap = [ ['Équipe', relatedData.equipe], ["Membres d'équipe", relatedData.membres], ['Planning', relatedData.planning], ['Tâches', relatedData.taches] ];
      relatedMap.forEach(([title, rows]) => {
        if (!rows || !rows.length) return;
        sectionHeader(title);
        rows.forEach(r => {
          const kv = Object.entries(r)
            .filter(([k,v]) => v!=null && typeof v !== 'object' && !/^id/i.test(k))
            .map(([k,v]) => {
              let val = String(v);
              if (isISODateTime(val)) val = formatDate(val); else if (isISODate(val)) val = formatDate(val);
              // numeric detection
              if (/^\d{4,}$/.test(val)) val = fmtNumber(val);
              return `${prettifyKey(k)}: ${val}`;
            });
          if (!kv.length) return; paragraph(kv.join(' '));
        });
      });

      // Budget as professional table
      if (relatedData.budget && relatedData.budget.length) {
        sectionHeader('Budget du Projet');
        drawBudgetTable(relatedData.budget);
      }

      // Final page for comments & notes
      {
        const hasEvalData = criteres.length && (Object.keys(comments).length || Object.keys(notes).length);
        // Local full-fill check (avoid depending on external function ordering)
        const doneEval = alreadyEvaluated || (
          criteres.length > 0 &&
          criteres.every(cr =>
            (cr.sous_criteres || []).every(sc => {
              const cVal = (comments[sc.ID_sous_critres] || '').trim();
              const nVal = (notes[sc.ID_sous_critres] || '').toString().trim();
              return cVal.length > 0 && nVal.length > 0;
            })
          )
        );

        if (hasEvalData && doneEval) {
          doc.addPage();
          drawHeader();
          y = HEADER_H + 26;
          sectionHeader('Commentaires & Notes (Synthèse)');
          criteres.forEach(cr => {
            const sousList = (cr.sous_criteres || []).filter(sc =>
              (comments[sc.ID_sous_critres] || '').trim() ||
              (notes[sc.ID_sous_critres] !== undefined && notes[sc.ID_sous_critres] !== '')
            );
            if (!sousList.length) return;
            ensureSpace(24);
            doc.setFont('helvetica','bold');
            doc.setTextColor(ACCENT.r, ACCENT.g, ACCENT.b);
            doc.text(cr.Titre_criteres, MARGIN_X, y);
            y += 18;
            doc.setFont('helvetica','normal');
            doc.setTextColor(40,40,40);
            sousList.forEach(sc => {
              ensureSpace(40);
              let lineTitle = sc.Titre_sous_critres;
              const noteVal = notes[sc.ID_sous_critres];
              if (noteVal !== undefined && noteVal !== '') {
                lineTitle += ` (Note: ${noteVal})`;
              }
              doc.setFont('helvetica','bold');
              doc.text('• ' + lineTitle, MARGIN_X, y);
              y += 14;
              doc.setFont('helvetica','normal');
              const com = (comments[sc.ID_sous_critres] || '').trim();
              if (com) {
                // Reuse paragraph box styling for each comment
                paragraph(com);
              } else {
                y += 4;
              }
            });
            y += 6;
          });
        }
      }

      addFooter();
      doc.save(`projet_${projectInfo.idprojet || 'export'}.pdf`);
    } catch (err) {
      console.error('PDF generation error', err);
      alert("Impossible de générer le PDF. Assurez-vous que 'jspdf' est installé.");
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        setRelatedLoaded(false);
        const apiBase =
          process.env.REACT_APP_API_BASE ||
          process.env.REACT_APP_BACKEND_URL ||
          `${window.location.protocol}//${window.location.hostname}:3001`;

        // Fetch forum data (evaluation, criteres, sous-criteres)
        const res = await fetch(
          `${apiBase}/api/evaluateur/competitions/${encodeURIComponent(
            idcompetition
          )}/forum-data`
        );
        const json = await res.json();
        if (json.status !== "success") {
          setError(json.message || "Erreur de chargement");
        } else {
          setEvaluationId(json.ID_Evaluation || null);
          setEvaluationTitle(json.evaluation?.Titre_evaluation || "");
          setCriteres(Array.isArray(json.criteres) ? json.criteres : []);
        }

        // Fetch project info & related
        if (idprojet) {
          const pRes = await fetch(
            `${apiBase}/api/evaluateur/competitions/${encodeURIComponent(
              idcompetition
            )}/projets/${encodeURIComponent(idprojet)}`
          );
          const pj = await pRes.json();
          if (pj.status === "success") setProjectInfo(pj.projet || null);
          const relRes = await fetch(
            `${apiBase}/api/evaluateur/projets/${encodeURIComponent(
              idprojet
            )}/related`
          );
          const relJson = await relRes.json();
          if (relJson.status === "success") {
            const newData = {
              equipe: relJson.equipe || [],
              membres: relJson.membres || [],
              planning: relJson.planning || [],
              taches: relJson.taches || [],
              budget: relJson.budget || [],
            };
            setRelatedData(newData);
            console.log("Related data fetched:", newData);
          }
          setRelatedLoaded(true);
        }
      } catch (e) {
        setError(String(e));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [idcompetition, idprojet]);

  useEffect(() => {
    const fetchCompetitionTitle = async () => {
      try {
        const apiBase =
          process.env.REACT_APP_API_BASE ||
          process.env.REACT_APP_BACKEND_URL ||
          `${window.location.protocol}//${window.location.hostname}:3001`;
        const res = await fetch(
          `${apiBase}/api/evaluateur/competitions/${encodeURIComponent(
            idcompetition
          )}`
        );
        const json = await res.json();
        if (json.status === "success" && json.competition) {
          setCompetitionTitle(
            json.competition.titre || json.competition.libelle || ""
          );
        }
      } catch {
        /* silent */
      }
    };
    fetchCompetitionTitle();
  }, [idcompetition]);

  // Prefill previously saved evaluations (per evaluator) + detect lock
  useEffect(() => {
    if (!idprojet || !idcompetition) return;
    (async () => {
      try {
        const apiBase =
          process.env.REACT_APP_API_BASE ||
          process.env.REACT_APP_BACKEND_URL ||
          `${window.location.protocol}//${window.location.hostname}:3001`;

        const getCookie = (name) => {
          const m = document.cookie.split(';').find(c=>c.trim().startsWith(name+'='));
          return m ? decodeURIComponent(m.split('=')[1]) : null;
        };
        const idevaluateur = getCookie('idevaluateur');

        const r = await fetch(
          `${apiBase}/api/project-evaluation?id_projet=${encodeURIComponent(
            idprojet
          )}&id_competition=${encodeURIComponent(idcompetition)}${idevaluateur ? `&idevaluateur=${encodeURIComponent(idevaluateur)}`:''}`
        );
        const j = await r.json();
        if (j.status === "success" && Array.isArray(j.data) && j.data.length) {
          const cMap = {};
          const nMap = {};
          j.data.forEach(row => {
            cMap[row.id_sous_critere] = row.commentaire || "";
            nMap[row.id_sous_critere] = row.note;
          });
          setComments(cMap);
          setNotes(nMap);
          setAlreadyEvaluated(true); // lock
        } else {
          setAlreadyEvaluated(false);
        }
      } catch {
        /* silent */
      }
    })();
  }, [idprojet, idcompetition]);

  // NEW: compute max per sous-critère from critères.sous_criteres.note (if present in forum-data)
  useEffect(() => {
    const map = {};
    (criteres || []).forEach(cr =>
      (cr.sous_criteres || []).forEach(sc => {
        const raw = sc?.note;
        const mx = raw === null || raw === undefined || raw === "" ? NaN : Number(raw);
        if (!isNaN(mx) && mx > 0) {
          map[sc.ID_sous_critres] = mx;
        }
      })
    );
    if (Object.keys(map).length) setMaxNoteBySous(map);
  }, [criteres]);

  // NEW: if forum-data doesn't include note, fetch it from /api/evaluation/list and map by evaluationId
  useEffect(() => {
    if (!evaluationId) return;
    (async () => {
      try {
        const apiBase =
          process.env.REACT_APP_API_BASE ||
          process.env.REACT_APP_BACKEND_URL ||
          `${window.location.protocol}//${window.location.hostname}:3001`;
        const r = await fetch(`${apiBase}/api/evaluation/list`);
        const j = await r.json();
        if (j?.status === "success" && Array.isArray(j.data)) {
          const evalItem = j.data.find(
            (e) => String(e.ID_Evaluation) === String(evaluationId)
          );
          if (evalItem && Array.isArray(evalItem.criteria)) {
            const map = {};
            evalItem.criteria.forEach((c) =>
              (c.subCriteria || []).forEach((s) => {
                const mx = Number(s.note);
                if (!isNaN(mx) && mx > 0) map[s.ID_sous_critres] = mx;
              })
            );
            if (Object.keys(map).length) setMaxNoteBySous(map);
          }
        }
      } catch {/* silent */}
    })();
  }, [evaluationId]);

  // UPDATE: cap note by per-sous-critère max instead of hard 20; round to integer (step=1)
  const handleNote = (idSous, val) => {
    let num = parseFloat(val);
    if (isNaN(num)) {
      setNotes((s) => ({ ...s, [idSous]: '' }));
      return;
    }
    const max = maxNoteBySous[idSous] ?? 20;
    if (num > max) num = max;
    if (num < 0) num = 0;
    num = Math.round(num); // align with step=1
    setNotes((s) => ({ ...s, [idSous]: num }));
  };

  const handleEvaluerClick = async () => {
    if (alreadyEvaluated) return; // prevent re-submit
    if (!allSousFilledGlobal()) return;
    try {
      const apiBase =
        process.env.REACT_APP_API_BASE ||
        process.env.REACT_APP_BACKEND_URL ||
        `${window.location.protocol}//${window.location.hostname}:3001`;

      const evaluationsPayload = [];
      criteres.forEach(cr => {
        (cr.sous_criteres || []).forEach(sc => {
          evaluationsPayload.push({
            id_sous_critere: sc.ID_sous_critres,
            id_critere: cr.ID_criteres,
            commentaire: comments[sc.ID_sous_critres] || "",
            note: notes[sc.ID_sous_critres]
          });
        });
      });

      console.log("Sending evaluation payload:", {
        id_projet: idprojet,
        id_competition: idcompetition,
        evaluations: evaluationsPayload
      });

      const getCookie = (name) => {
        const m = document.cookie.split(';').find(c=>c.trim().startsWith(name+'='));
        return m ? decodeURIComponent(m.split('=')[1]) : null;
      };
      const idevaluateur = parseInt(getCookie('idevaluateur'), 10) || null;
      const res = await fetch(`${apiBase}/api/project-evaluation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id_projet: parseInt(idprojet, 10),
          id_competition: parseInt(idcompetition, 10),
          idevaluateur,
          evaluations: evaluationsPayload
        })
      });
      const json = await res.json();
      console.log("Save response:", json);
      if (json.status === "success") {
        // Optional: alert can be kept or removed
        // alert("Evaluation enregistrée avec succès.");
        navigate(`/evaluateur/competitions/${idcompetition}/projets`);
      } else {
        alert("Erreur lors de l'enregistrement: " + (json.message || ""));
      }
    } catch (e) {
      console.error("Save error:", e);
      alert("Erreur réseau / serveur.");
    }
  };

  const container = {
    display: "flex",
    minHeight: "100vh",
    background: "#e4d2d2",
  };
  const content = {
    flex: 1,
    padding: 24,
    marginLeft: "17rem",
    width: "calc(100vw - 20rem)",
  };
  const headerBar = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#fff",
    border: "1px solid #e6e8eb",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 8px 16px rgba(16,24,40,0.08)",
    marginBottom: 16,
  };
  const backBtn = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #d0d5dd",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  };
  const downloadBtn = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid #0b7a3e",
    background: "#0b7a3e",
    color: "#fff",
    fontWeight: 700,
    cursor: projectInfo && relatedLoaded ? "pointer" : "not-allowed",
    opacity: projectInfo && relatedLoaded ? 1 : 0.5,
  };

  // Polished styles
  const pageTitle = {
    textAlign: "center",
    fontSize: "2rem",
    fontWeight: 900,
    color: "#0b7a3e",
    margin: "12px 0 28px",
    letterSpacing: 0.2,
  };
  // Replace simple subTitle with a richer layout
  const projectInfoWrap = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    marginTop: -12,
    marginBottom: 28,
  };
  const projectName = { fontWeight: 800, fontSize: "1.1rem", color: "#0f172a" };
  const metaRow = {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "center",
  };
  const metaPill = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 999,
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 600,
  };
  const formWrap = { maxWidth: 1000, margin: "0 auto" };
  const critHeading = {
    fontSize: "1.25rem",
    fontWeight: 800,
    textAlign: "left",
    color: "#0f172a",
    margin: "28px 0 12px",
    paddingLeft: 10,
    borderLeft: "4px solid #0b7a3e",
  };
  const scBox = {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    boxShadow: "0 4px 12px rgba(16,24,40,0.06)",
  };
  const label = {
    display: "block",
    marginTop: 4,
    marginBottom: 6,
    fontWeight: 700,
    color: "#0f172a",
  };
  const textarea = {
    width: "100%",
    minHeight: 110,
    borderRadius: 10,
    border: "1px solid #d0d5dd",
    padding: 12,
    background: "#fff",
  };
  const noteLabel = { ...label, marginTop: 10 };
  const noteInput = {
    width: 110,
    borderRadius: 8,
    border: "1px solid #d0d5dd",
    padding: "6px 10px",
    background: "#fff",
  };
  const errorBox = {
    background: "#fee2e2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
  };
  const navBar = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 24,
    gap: 12,
  };
  const btnBase = {
    padding: "10px 18px",
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    border: "1px solid transparent",
    transition: "background .15s",
  };
  const prevBtnStyle = (disabled) => ({
    ...btnBase,
    background: disabled ? "#f1f5f9" : "#fff",
    color: disabled ? "#94a3b8" : "#0f172a",
    borderColor: "#d0d5dd",
    cursor: disabled ? "not-allowed" : "pointer",
  });
  const nextBtnStyle = (disabled, last) => ({
    ...btnBase,
    background: disabled ? "#e2e8f0" : "#0b7a3e",
    color: disabled ? "#64748b" : "#fff",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: last ? 0.85 : 1,
  });

  const lockNotice = {
    background: "#fde68a",
    border: "1px solid #f59e0b",
    color: "#92400e",
    padding: "10px 14px",
    borderRadius: 10,
    fontWeight: 600,
    marginBottom: 20
  };

  const allSousFilledForCurrent = () => {
    const current = criteres[currentCritereIndex];
    if (!current) return false;
    const sousList = current.sous_criteres || [];
    if (!sousList.length) return true; // nothing to fill
    return sousList.every((sc) => {
      const cVal = (comments[sc.ID_sous_critres] || "").trim();
      const nVal = (notes[sc.ID_sous_critres] || "").toString().trim();
      return cVal.length > 0 && nVal.length > 0;
    });
  };

  const allSousFilledGlobal = () => {
    if (!criteres.length) return false;
    return criteres.every(cr => (cr.sous_criteres || []).every(sc => {
      const cVal = (comments[sc.ID_sous_critres] || '').trim();
      const nVal = (notes[sc.ID_sous_critres] || '').toString().trim();
      return cVal.length > 0 && nVal.length > 0;
    }));
  };

  const evalBtnStyle = (enabled) => ({
    padding: '12px 34px',
    borderRadius: 12,
    fontWeight: 700,
    background: enabled ? '#0b7a3e' : '#cbd5e1',
    color: enabled ? '#ffffff' : '#64748b',
    cursor: enabled ? 'pointer' : 'not-allowed',
    border: 'none',
    fontSize: 16,
    letterSpacing: '.5px',
    boxShadow: enabled ? '0 4px 14px rgba(11,122,62,0.35)' : 'none',
    transition: 'background .2s, box-shadow .2s'
  });

  // const handleEvaluerClick = async () => {
  //   if (!allSousFilledGlobal()) return;
  //   try {
  //     const apiBase =
  //       process.env.REACT_APP_API_BASE ||
  //       process.env.REACT_APP_BACKEND_URL ||
  //       `${window.location.protocol}//${window.location.hostname}:3001`;

  //     // Build evaluations array
  //     const evaluationsPayload = [];
  //     criteres.forEach(cr => {
  //       (cr.sous_criteres || []).forEach(sc => {
  //         evaluationsPayload.push({
  //           id_sous_critere: sc.ID_sous_critres,
  //           id_critere: cr.ID_criteres,
  //           commentaire: comments[sc.ID_sous_critres] || "",
  //           note: notes[sc.ID_sous_critres]
  //         });
  //       });
  //     });

  //     const res = await fetch(`${apiBase}/api/project-evaluation`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({
  //         id_projet: parseInt(idprojet, 10),
  //         id_competition: parseInt(idcompetition, 10),
  //         evaluations: evaluationsPayload
  //       })
  //     });
  //     const json = await res.json();
  //     if (json.status === "success") {
  //       alert("Evaluation enregistrée avec succès.");
  //     } else {
  //       alert("Erreur lors de l'enregistrement: " + (json.message || ""));
  //     }
  //   } catch (e) {
  //     alert("Erreur réseau / serveur.");
  //   }
  // };

  const goNext = () => {
    if (
      currentCritereIndex < criteres.length - 1 &&
      allSousFilledForCurrent()
    ) {
      setCurrentCritereIndex((i) => i + 1);
    }
  };
  const goPrev = () => {
    if (currentCritereIndex > 0) setCurrentCritereIndex((i) => i - 1);
  };

  return (
    <div style={container}>
      <Sidebar />
      <div style={content}>
        {/* Global input focus styles for a refined look */}
        <style>{`
          .fe-textarea, .fe-note { outline: none; transition: border-color .15s ease, box-shadow .15s ease; }
          .fe-textarea::placeholder, .fe-note::placeholder { color: #9aa4b2; }
          .fe-textarea:focus, .fe-note:focus { border-color: #0b7a3e !important; box-shadow: 0 0 0 3px rgba(11,122,62,0.15); }
        `}</style>

        <div style={headerBar}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <button style={backBtn} onClick={() => navigate(-1)}>
              <FaArrowLeft /> Retour
            </button>
            {(competitionTitle || projectInfo) && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {competitionTitle && (
                  <span
                    style={{
                      fontSize: "1.05rem",
                      fontWeight: 800,
                      color: "#0f172a",
                    }}
                  >
                    {competitionTitle}
                  </span>
                )}
                {competitionTitle && projectInfo && (
                  <span style={{ color: "#64748b", fontWeight: 600 }}>›</span>
                )}
                {projectInfo && (
                  <span
                    style={{
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      color: "#0b7a3e",
                    }}
                  >
                    {projectInfo.titre ||
                      projectInfo.titre_projet ||
                      `Projet #${projectInfo.idprojet}`}
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            style={downloadBtn}
            onClick={handleDownloadPdf}
            disabled={!(projectInfo && relatedLoaded)}
          >
            Télécharger
          </button>
        </div>

        {loading && <div>Chargement...</div>}
        {error && <div style={errorBox}>{error}</div>}

        {!!evaluationTitle && <div style={pageTitle}>{evaluationTitle}</div>}
        {projectInfo && (
          <div style={projectInfoWrap}>
            {/* Removed duplicate project name (now in header) */}
            <div style={metaRow}>
              <span style={metaPill}>
                <strong>Soumis par:</strong>{" "}
                {[projectInfo.prenom, projectInfo.nom]
                  .filter(Boolean)
                  .join(" ") ||
                  projectInfo.responsable ||
                  "-"}
              </span>
              <span style={metaPill}>
                <strong>Soumis le:</strong>{" "}
                {
                  String(
                    projectInfo.date_debut_projet ||
                      projectInfo.date_creation ||
                      ""
                  ).split("T")[0]
                }
              </span>
            </div>
          </div>
        )}

        {alreadyEvaluated && (
          <div style={lockNotice}>
            Vous avez déjà évalué ce projet. Lecture seule.
          </div>
        )}

        <div style={formWrap}>
          {criteres.length <= 1 &&
            criteres.map((c) => (
              <div key={c.ID_criteres}>
                <h3 style={critHeading}>{c.Titre_criteres}</h3>
                {(c.sous_criteres || []).map((s) => (
                  <div key={s.ID_sous_critres} style={scBox}>
                    <label style={label}> {s.Titre_sous_critres}</label>
                    <textarea
                      className="fe-textarea"
                      style={textarea}
                      value={comments[s.ID_sous_critres] || ""}
                      onChange={alreadyEvaluated ? undefined : (e) =>
                        setComments((st) => ({
                          ...st,
                          [s.ID_sous_critres]: e.target.value,
                        }))
                      }
                      placeholder="Votre commentaire..."
                      disabled={alreadyEvaluated}
                    />
                    <label style={noteLabel}>Note</label>
                    <input
                      className="fe-note"
                      type="number"
                      min="0"
                      max={maxNoteBySous[s.ID_sous_critres] || 20}
                      step="1"
                      style={noteInput}
                      value={notes[s.ID_sous_critres] || ""}
                      onChange={alreadyEvaluated ? undefined : (e) => handleNote(s.ID_sous_critres, e.target.value)}
                      onBlur={alreadyEvaluated ? undefined : (e) => handleNote(s.ID_sous_critres, e.target.value)}
                      disabled={alreadyEvaluated}
                      title={`Note maximale: ${maxNoteBySous[s.ID_sous_critres] || 20}`}
                    />
                    <span style={{ marginLeft: 8, fontWeight: 700, color: '#475569' }}>
                      sur {maxNoteBySous[s.ID_sous_critres] || 20}
                    </span>
                  </div>
                ))}
              </div>
            ))}

          {criteres.length > 1 &&
            (() => {
              const c = criteres[currentCritereIndex];
              return c ? (
                <>
                  <h3 style={critHeading}>{c.Titre_criteres}</h3>
                  {(c.sous_criteres || []).map((s) => (
                    <div key={s.ID_sous_critres} style={scBox}>
                      <label style={label}>{s.Titre_sous_critres}</label>
                      <textarea
                        className="fe-textarea"
                        style={textarea}
                        value={comments[s.ID_sous_critres] || ""}
                        onChange={alreadyEvaluated ? undefined : (e) =>
                          setComments((st) => ({
                            ...st,
                            [s.ID_sous_critres]: e.target.value,
                          }))
                        }
                        placeholder="Votre commentaire..."
                        disabled={alreadyEvaluated}
                      />
                      <label style={noteLabel}>Note</label>
                      <input
                        className="fe-note"
                        type="number"
                        min="0"
                        max={maxNoteBySous[s.ID_sous_critres] || 20}
                        step="1"
                        style={noteInput}
                        value={notes[s.ID_sous_critres] || ""}
                        onChange={alreadyEvaluated ? undefined : (e) => handleNote(s.ID_sous_critres, e.target.value)}
                        onBlur={alreadyEvaluated ? undefined : (e) => handleNote(s.ID_sous_critres, e.target.value)}
                        disabled={alreadyEvaluated}
                        title={`Note maximale: ${maxNoteBySous[s.ID_sous_critres] || 20}`}
                      />
                      <span style={{ marginLeft: 8, fontWeight: 700, color: '#475569' }}>
                        sur {maxNoteBySous[s.ID_sous_critres] || 20}
                      </span>
                    </div>
                  ))}
                  <div style={navBar}>
                    <button
                      type="button"
                      onClick={goPrev}
                      disabled={currentCritereIndex === 0}
                      style={prevBtnStyle(currentCritereIndex === 0)}
                    >
                      Précédent
                    </button>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#475569",
                      }}
                    >
                      Critère {currentCritereIndex + 1} / {criteres.length}
                    </div>
                    {currentCritereIndex < criteres.length - 1 && (
                      <button
                        type="button"
                        onClick={goNext}
                        disabled={!allSousFilledForCurrent()}
                        style={nextBtnStyle(!allSousFilledForCurrent(), false)}
                      >
                        Suivant
                      </button>
                    )}
                    {currentCritereIndex === criteres.length - 1 && !alreadyEvaluated && (
                      <button
                        type="button"
                        disabled={!allSousFilledGlobal()}
                        style={nextBtnStyle(!allSousFilledGlobal(), true)}
                        onClick={handleEvaluerClick}
                      >
                        Évaluer
                      </button>
                    )}
                  </div>
                </>
              ) : null;
            })()}
          {criteres.length <= 1 && !alreadyEvaluated && (
            <div style={{ textAlign: 'center', marginTop: 48 }}>
              <button
                type="button"
                disabled={!allSousFilledGlobal()}
                style={evalBtnStyle(allSousFilledGlobal())}
                onClick={handleEvaluerClick}
              >
                Evaluer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForumEvaluation;
